import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { staticifyDeliveryRows, stripDeliveryDrawerFromModal } from '@theme/dev-delivery-info';
import { initProductDescriptionTruncates } from '@theme/dev-product-description-truncate';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, onAnimationEnd } from '@theme/utilities';
// Side effects: capture click + MutationObserver for size/swatch drawers.
// Must load with quick-add — section HTML <script> tags do not execute after morph.
import '@theme/dev-variant-drawer';

/** Section Rendering API id for `sections/dev-quick-add.liquid`. */
const QUICK_ADD_SECTION_ID = 'dev-quick-add';

/** @type {Map<string, Element>} */
const productGridCache = new Map();

/**
 * Prefer data-* hooks; keep class fallbacks for older morph HTML.
 * @param {ParentNode} root
 * @param {string} dataSelector
 * @param {string} [classFallback]
 * @returns {Element | null}
 */
function qaFind(root, dataSelector, classFallback) {
  return root.querySelector(dataSelector) || (classFallback ? root.querySelector(classFallback) : null);
}

/**
 * Product pathname cache key (variant + section_id must not fragment the base cache).
 * @param {string} urlString
 * @returns {string}
 */
function productCacheKey(urlString) {
  const url = new URL(canonicalProductPageUrl(urlString), window.location.origin);
  url.searchParams.delete('variant');
  url.searchParams.delete('section_id');
  url.searchParams.delete('sections');
  return `${url.pathname}${url.search}`;
}

/**
 * Section Rendering for `dev-quick-add` only works on `/products/{handle}`.
 * Collection cards (and metafield product refs rendered in that context) often
 * yield `/collections/{col}/products/{handle}` — that 404s the section on PLP.
 * @param {string} urlString
 * @returns {string}
 */
function canonicalProductPageUrl(urlString) {
  const url = new URL(urlString, window.location.origin);
  const parts = url.pathname.split('/').filter(Boolean);
  const productsIdx = parts.lastIndexOf('products');
  const handle = productsIdx >= 0 ? parts[productsIdx + 1] : '';

  if (handle) {
    const locale = parts[0] && parts[0] !== 'products' && parts[0] !== 'collections' ? parts[0] : '';
    url.pathname = locale ? `/${locale}/products/${handle}` : `/products/${handle}`;
  }

  return url.toString();
}

/**
 * @param {string} productPageUrl
 * @returns {string}
 */
function buildQuickAddSectionUrl(productPageUrl) {
  const url = new URL(canonicalProductPageUrl(productPageUrl), window.location.origin);
  url.searchParams.set('section_id', QUICK_ADD_SECTION_ID);
  // `/{lang}` fetch returns a Weglot-proxy-translated fragment — dialog paints translated HTML (no EN flash).
  const built = url.toString();
  return window.NoomaWeglotChrome?.prefixFetchUrl?.(built) ?? built;
}

/**
 * @param {Document | ParentNode} root
 * @returns {Element | null}
 */
function extractQuickAddGrid(root) {
  return (
    root.querySelector('[data-quick-add-grid]') ||
    root.querySelector('.quick-add-grid') ||
    root.querySelector(`#shopify-section-${QUICK_ADD_SECTION_ID} [data-quick-add-grid]`)
  );
}

/**
 * Dialog scope — on mobile the gallery is shelled outside `#quick-add-modal-content`.
 * @param {ParentNode} root
 * @returns {ParentNode}
 */
function quickAddDialogScope(root) {
  if (root instanceof Element) {
    return root.closest('dialog.quick-add-modal') || root;
  }
  return root;
}

/**
 * Ensure gallery chrome structure (badge + wishlist overlays).
 * Preferred path: Liquid already emits chrome + scroll in `dev-quick-add-content`.
 * This stays as a defensive guard for morph / partial HTML edge cases — avoid moving
 * <media-gallery> when structure is already correct (prevents CE disconnect).
 * @param {ParentNode} root
 */
function pinGalleryChrome(root) {
  const scope = quickAddDialogScope(root);
  const media =
    qaFind(scope, '[data-product-media-column]', '.quick-add-grid__media') ||
    qaFind(root, '[data-product-media-column]', '.quick-add-grid__media');
  if (!(media instanceof HTMLElement)) return;

  let scroll = media.querySelector(':scope > [data-quick-add-media-scroll]');
  let chrome = media.querySelector(':scope > [data-quick-add-media-chrome]');

  const structureReady =
    scroll instanceof HTMLElement &&
    chrome instanceof HTMLElement &&
    chrome.contains(media.querySelector('[data-quick-add-badges]')) &&
    chrome.contains(media.querySelector('[data-quick-add-wishlist]'));

  if (!structureReady) {
    if (!(scroll instanceof HTMLElement)) {
      scroll = document.createElement('div');
      scroll.className = 'quick-add-modal__media-scroll';
      scroll.setAttribute('data-quick-add-media-scroll', '');

      const pending = Array.from(media.childNodes);
      for (const node of pending) {
        if (node instanceof HTMLElement && node.hasAttribute('data-quick-add-media-chrome')) continue;
        scroll.appendChild(node);
      }
      media.appendChild(scroll);
    }

    if (!(chrome instanceof HTMLElement)) {
      chrome = document.createElement('div');
      chrome.className = 'quick-add-modal__media-chrome';
      chrome.setAttribute('data-quick-add-media-chrome', '');
    }
    if (chrome.parentElement !== media) {
      media.appendChild(chrome);
    }

    const badges =
      media.querySelector('[data-quick-add-badges]') ||
      scroll.querySelector('[data-quick-add-badges]');
    const wishlist =
      media.querySelector('[data-quick-add-wishlist]') ||
      scroll.querySelector('[data-quick-add-wishlist]');

    if (badges instanceof HTMLElement && badges.parentElement !== chrome) {
      for (const old of chrome.querySelectorAll('[data-quick-add-badges]')) {
        if (old !== badges) old.remove();
      }
      chrome.appendChild(badges);
    }
    if (wishlist instanceof HTMLElement && wishlist.parentElement !== chrome) {
      for (const old of chrome.querySelectorAll('[data-quick-add-wishlist]')) {
        if (old !== wishlist) old.remove();
      }
      chrome.appendChild(wishlist);
    }
  }

  dedupeQuickAddWishlist(scope);
  syncQuickAddWishlist(scope);
}

/**
 * Keep a single headless heart in chrome; drop WK CE / duplicate slots (mobile shell + late inject).
 * @param {ParentNode} root
 */
function dedupeQuickAddWishlist(root) {
  const scope = quickAddDialogScope(root);

  for (const stray of scope.querySelectorAll(
    '[data-dev-wishlist-gallery-slot], [data-dev-wishlist-slot], wishlist-button-product, .wk-floating-wishlist'
  )) {
    if (stray instanceof HTMLElement) {
      stray.remove();
    }
  }

  const hearts = [...scope.querySelectorAll('[data-quick-add-wishlist]')];
  if (hearts.length <= 1) return;

  const keep =
    scope.querySelector('[data-quick-add-media-chrome] [data-quick-add-wishlist]') || hearts[0];

  for (const heart of hearts) {
    if (heart !== keep) heart.remove();
  }
}

/**
 * Hydrate the theme headless heart after morph (Wishlist King does not auto-scan section HTML).
 * @param {ParentNode} root
 */
function syncQuickAddWishlist(root) {
  const scope = quickAddDialogScope(root);
  dedupeQuickAddWishlist(scope);

  const slot =
    scope.querySelector('[data-quick-add-media-chrome] [data-quick-add-wishlist]') ||
    scope.querySelector('[data-quick-add-wishlist]');
  if (!(slot instanceof HTMLElement)) return;

  try {
    window.WishlistKing?.theme?.observer?.scan?.(slot);
  } catch {
    // Ignore — some WK page routers throw; idle heart still renders.
  }
}

/**
 * After variant:update gallery swap — re-assert chrome (defensive), mobile sizes, WK scan.
 * @param {ParentNode} root
 */
function refreshQuickAddGalleryAfterVariant(root) {
  pinGalleryChrome(root);
  configureMobileQuickAddGallery(root);
  syncQuickAddWishlist(root);
}

/**
 * Mobile Quick Add gallery must be a short portrait strip — not the PDP full-bleed carousel.
 * PDP CSS (`.nooma-gallery-viewport` height chain + `--slide-width: 100%` on
 * `slideshow-slide.product-media-container`) wins over stylesheet tweaks; set sizes in JS.
 * @param {ParentNode} root
 */
function configureMobileQuickAddGallery(root) {
  if (!isMobileBreakpoint()) return;

  const scope = quickAddDialogScope(root);
  const media =
    qaFind(scope, '[data-product-media-column]', '.quick-add-grid__media') ||
    qaFind(root, '[data-product-media-column]', '.quick-add-grid__media');
  if (!(media instanceof HTMLElement)) return;
  media.setAttribute('data-quick-add-gallery', '');

  const viewport = media.querySelector('.nooma-gallery-viewport');
  if (viewport instanceof HTMLElement) {
    viewport.style.setProperty('height', 'auto', 'important');
    viewport.style.setProperty('max-height', 'none', 'important');
    viewport.style.setProperty('min-height', '0', 'important');
    viewport.style.setProperty('overflow', 'visible', 'important');
  }

  const slideWidth = Math.min(168, Math.round(window.innerWidth * 0.44));
  const slideHeight = Math.round((slideWidth * 4) / 3);

  media.style.setProperty('--qa-slide-w', `${slideWidth}px`);
  media.style.setProperty('--qa-slide-h', `${slideHeight}px`);
  media.style.setProperty('--slide-width', `${slideWidth}px`);
  media.style.setProperty('--gallery-aspect-ratio', '3 / 4');

  const slides = media.querySelector('slideshow-slides');
  if (slides instanceof HTMLElement) {
    slides.style.setProperty('height', `${slideHeight}px`, 'important');
    slides.style.setProperty('min-height', `${slideHeight}px`, 'important');
    slides.style.setProperty('display', 'flex', 'important');
    slides.style.setProperty('flex-direction', 'row', 'important');
  }

  for (const slide of media.querySelectorAll('slideshow-slide')) {
    if (!(slide instanceof HTMLElement)) continue;
    slide.style.setProperty('width', `${slideWidth}px`, 'important');
    slide.style.setProperty('flex', `0 0 ${slideWidth}px`, 'important');
    slide.style.setProperty('height', `${slideHeight}px`, 'important');
    slide.style.setProperty('min-height', `${slideHeight}px`, 'important');
    slide.style.setProperty('max-height', 'none', 'important');
    slide.style.setProperty('aspect-ratio', '3 / 4', 'important');
    slide.style.setProperty('--slide-width', `${slideWidth}px`);
    slide.removeAttribute('hidden');
    slide.style.setProperty('display', 'block', 'important');
    // PDP marks inactive slides aria-hidden — still show them in the strip.
    if (slide.getAttribute('aria-hidden') === 'true') {
      slide.style.setProperty('visibility', 'visible', 'important');
      slide.style.setProperty('opacity', '1', 'important');
    }
  }

  for (const el of media.querySelectorAll('.product-media-container, .product-media')) {
    if (!(el instanceof HTMLElement)) continue;
    el.style.setProperty('width', '100%', 'important');
    el.style.setProperty('height', '100%', 'important');
    el.style.setProperty('max-height', 'none', 'important');
    el.style.setProperty('aspect-ratio', 'unset', 'important');
  }
}

/**
 * @param {HTMLElement} host
 * @returns {HTMLDialogElement | null}
 */
function getDrawerDialog(host) {
  if ('refs' in host) {
    const dialog = /** @type {{ refs?: { dialog?: HTMLDialogElement } }} */ (host).refs?.dialog;
    if (dialog instanceof HTMLDialogElement) return dialog;
  }
  const fallback = host.querySelector('dialog');
  return fallback instanceof HTMLDialogElement ? fallback : null;
}

/**
 * Move drawer host to be a direct child of `.quick-add-modal` so absolute positioning +
 * overflow clip are relative to the modal (nested showModal() escapes to the top layer).
 * @param {HTMLElement} host
 * @param {HTMLDialogElement} modal
 */
function portalDrawerIntoModal(host, modal) {
  if (host.parentElement === modal) return;

  const placeholder = document.createComment('qa-drawer-home');
  host.parentNode?.insertBefore(placeholder, host);
  /** @type {HTMLElement & { __qaDrawerPlaceholder?: Comment }} */ (host).__qaDrawerPlaceholder =
    placeholder;
  host.setAttribute('data-qa-drawer-ported', '');
  modal.appendChild(host);
}

/**
 * @param {HTMLElement} host
 */
function restoreDrawerFromModal(host) {
  const placeholder = /** @type {HTMLElement & { __qaDrawerPlaceholder?: Comment }} */ (host)
    .__qaDrawerPlaceholder;
  if (placeholder?.parentNode) {
    placeholder.parentNode.insertBefore(host, placeholder);
    placeholder.remove();
  }
  delete /** @type {HTMLElement & { __qaDrawerPlaceholder?: Comment }} */ (host).__qaDrawerPlaceholder;
  host.removeAttribute('data-qa-drawer-ported');
  host.removeAttribute('data-qa-drawer-open');
}

/**
 * Force-close ported size drawers (no animation) and restore them into the morph tree.
 * Must run before modal close / remorph — leftover `dialog[open]` used to steal scroll-lock.
 * @param {ParentNode} [scope]
 */
function forceClosePortedDrawers(scope = document) {
  for (const host of scope.querySelectorAll('[data-qa-drawer-ported]')) {
    if (!(host instanceof HTMLElement)) continue;

    const dialog = getDrawerDialog(host);
    if (dialog instanceof HTMLDialogElement) {
      cancelQaDrawerAnimation(dialog);
      if (dialog.open) {
        dialog.close();
      }
    }

    // close listener restores when open; if already closed, restore here.
    if (host.hasAttribute('data-qa-drawer-ported')) {
      restoreDrawerFromModal(host);
    }
  }
}

/**
 * Drop any running slide and the lifecycle classes that drive it, so a force-close cannot
 * leave the panel mid-travel.
 * @param {HTMLDialogElement} dialog
 */
function cancelQaDrawerAnimation(dialog) {
  dialog.getAnimations().forEach((animation) => animation.cancel());
  dialog.classList.remove('dialog-enter-hold', 'dialog-closing', 'dialog-visible');
}

/**
 * Release the entry animation once the frame that opened the panel has rendered.
 *
 * `DialogComponent.showDialog()`'s tail, copied because this panel opens with `dialog.show()`.
 * The `rAF → setTimeout` pair is load-bearing: only a task queued from a frame callback runs after
 * that frame has painted, which is what keeps the keyframe clock from starting early.
 * @param {HTMLDialogElement} dialog
 */
function releaseQaDrawerEntry(dialog) {
  requestAnimationFrame(() => {
    setTimeout(() => {
      dialog.classList.remove('dialog-enter-hold');

      if (dialog.open && dialog.hasAttribute('data-slide-right')) {
        dialog.classList.add('dialog-visible');
      }
    });
  });
}

/**
 * Slide the panel out and close it, mirroring `DialogComponent.closeDialog()`.
 * @param {HTMLDialogElement} dialog
 * @returns {Promise<void>}
 */
async function closeQaDrawer(dialog) {
  if (!dialog.open) return;

  // A second close (double-tap X) must await the running exit, not restart it — restarting cancels
  // the slide-out and the panel vanishes.
  if (dialog.classList.contains('dialog-closing')) {
    await onAnimationEnd(dialog, undefined, { subtree: false });
    return;
  }

  dialog.classList.remove('dialog-visible');
  dialog.classList.add('dialog-closing');

  // Drop the opening animation's `forwards` fill, then let the distinct close name apply.
  dialog.style.animation = 'none';
  void dialog.offsetWidth;
  dialog.style.animation = '';

  await new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve);
    });
  });

  await onAnimationEnd(dialog, undefined, { subtree: false });

  if (dialog.open) dialog.close();
}

/**
 * Point variant triggers at quick-add (not `#vo-drawer…/showDialog` → document.querySelector,
 * which opens the PDP drawer from the viewport edge). Also unique-ify drawer ids and patch
 * showDialog so any leftover `#…/showDialog` path cannot call showModal().
 * @param {Element} root
 */
function scopeVariantDrawersForQuickAdd(root) {
  const prefix = `qa-${Math.random().toString(36).slice(2, 9)}`;
  const quickAdd =
    root.closest('quick-add-dialog') || document.getElementById('quick-add-dialog');

  for (const comp of root.querySelectorAll(
    [
      '.variant-option dialog-component.dev-drawer',
      '.variant-option dialog-component[data-quick-add-variant-drawer]',
      // Additional swatches (Size / Scent / …) — same nested-panel treatment
      '[data-dev-as] dialog-component.dev-drawer',
      '[data-dev-as] dialog-component[data-quick-add-variant-drawer]',
    ].join(', ')
  )) {
    if (!(comp instanceof HTMLElement)) continue;

    const oldId = comp.id;
    comp.setAttribute('data-quick-add-variant-drawer', '');

    if (oldId && !oldId.startsWith('qa-')) {
      const newId = `${prefix}-${oldId}`;
      comp.id = newId;

      for (const trigger of root.querySelectorAll(`[aria-controls="${oldId}"]`)) {
        trigger.setAttribute('aria-controls', newId);
      }
    }

    // Body scroll-lock stays on quick-add only.
    const nestedDialog = getDrawerDialog(comp);
    nestedDialog?.removeAttribute('scroll-lock');

    // Belt-and-suspenders: even `#qa-…/showDialog` must not use showModal(),
    // and close must not run DialogComponent scroll-lock / adoptScrollLock.
    if (quickAdd instanceof HTMLElement && !/** @type {any} */ (comp).__qaShowPatched) {
      /** @type {any} */ (comp).__qaShowPatched = true;
      /** @type {any} */ (comp).showDialog = () => {
        if (typeof /** @type {any} */ (quickAdd).openVariantDrawerFromHost === 'function') {
          /** @type {any} */ (quickAdd).openVariantDrawerFromHost(comp);
        }
      };
      /** @type {any} */ (comp).closeDialog = () => {
        const dialog = getDrawerDialog(comp);
        if (!dialog?.open) return;
        closeQaDrawer(dialog);
      };
    }
  }

  // AS triggers use `#id/showDialog` in Liquid — retarget to the portaled QA opener.
  for (const trigger of root.querySelectorAll('[data-as-drawer-trigger]')) {
    trigger.setAttribute('data-variant-drawer-trigger', '');
  }

  // Id selector — reliable with #quick-add-dialog { display: contents }
  for (const trigger of root.querySelectorAll('[data-variant-drawer-trigger]')) {
    trigger.setAttribute('on:click', '#quick-add-dialog/openVariantDrawer');
  }

  // Always drawer + stacked label/trigger in modal (Figma: label above field).
  for (const fieldset of root.querySelectorAll('.variant-option--drawer-capable')) {
    fieldset.classList.add('variant-option--use-drawer');
  }

  warmVariantDrawerThumbnails(root);
}

/**
 * Load the nested panels' swatch thumbnails before the panel opens.
 *
 * They are `loading="lazy"` and a closed `<dialog>` never intersects the viewport, so otherwise the
 * fetch and decode land across the panel's own travel. Idle and low priority so this cannot compete
 * with the sheet's entry.
 * @param {Element} root
 */
function warmVariantDrawerThumbnails(root) {
  const warm = () => {
    for (const img of root.querySelectorAll(
      'dialog-component[data-quick-add-variant-drawer] img[loading="lazy"]'
    )) {
      if (!(img instanceof HTMLImageElement)) continue;
      img.fetchPriority = 'low';
      img.loading = 'eager';
      // Decode too — a display:none subtree would otherwise defer it to the panel's first frame.
      img.decode().catch(() => {});
    }
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(warm, { timeout: 1200 });
  } else {
    setTimeout(warm, 400);
  }
}

/** @type {WeakMap<QuickAddDialog, AbortController>} */
const headingScrollControllers = new WeakMap();

/**
 * Compact title in the modal titlebar only after the main heading leaves the scrollport
 * (same idea as `.dev-drawer--visual` / `dev-drawer--heading-scrolled`).
 * @param {QuickAddDialog} host
 */
function bindQuickAddHeadingScroll(host) {
  const modal = host.refs.dialog;
  if (!(modal instanceof HTMLDialogElement)) return;

  headingScrollControllers.get(host)?.abort();
  const abort = new AbortController();
  headingScrollControllers.set(host, abort);

  modal.classList.remove('quick-add-modal--heading-scrolled');

  const scrollRoot = isMobileBreakpoint()
    ? host.querySelector('[data-quick-add-modal-content]')
    : host.querySelector('[data-quick-add-details]') ||
      host.querySelector('.quick-add-grid__details');
  if (!(scrollRoot instanceof HTMLElement)) return;

  const heading =
    host.querySelector('[data-quick-add-header] [data-quick-add-heading]') ||
    host.querySelector('a[data-quick-add-heading]') ||
    host.querySelector('[data-quick-add-heading]');
  if (!(heading instanceof HTMLElement)) return;

  // IntersectionObserver avoids false positives from absolute titlebar / open animation
  // (getBoundingClientRect could mark the title as "scrolled" while still on screen).
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry) return;
      modal.classList.toggle('quick-add-modal--heading-scrolled', !entry.isIntersecting);
    },
    {
      root: scrollRoot,
      threshold: 0,
    }
  );

  observer.observe(heading);
  abort.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
}

/**
 * Drop the PDP sticky ATC if the section HTML ever carries it into the modal. It is
 * `position: fixed`, so it would float over the sheet's own pinned footer instead of scrolling
 * with the stack. Layout of that footer is CSS-only — see `.buy-buttons-block` in
 * `snippets/quick-add-modal.liquid`.
 * @param {Element} modalContent
 */
function stripPageStickyAtcFromModal(modalContent) {
  if (!(modalContent instanceof HTMLElement)) return;

  const dialog = modalContent.closest('dialog.quick-add-modal');
  if (!(dialog instanceof HTMLDialogElement)) return;

  for (const sticky of dialog.querySelectorAll('sticky-add-to-cart, .sticky-add-to-cart__bar')) {
    sticky.remove();
  }
}

/**
 * @param {Element} modalContent
 */
function enhanceQuickAddModal(modalContent) {
  stripDeliveryDrawerFromModal(modalContent);
  staticifyDeliveryRows(modalContent);
  pinGalleryChrome(modalContent);
  configureMobileQuickAddGallery(modalContent);
  stripPageStickyAtcFromModal(modalContent);
  initProductDescriptionTruncates(modalContent);
  syncQuickAddWishlist(modalContent);

  // WK may inject a second heart after morph — sweep once the microtask queue settles.
  const dialog = quickAddDialogScope(modalContent);
  requestAnimationFrame(() => {
    dedupeQuickAddWishlist(dialog);
    window.setTimeout(() => dedupeQuickAddWishlist(dialog), 400);
  });
}

/**
 * Sync opener variant into modal picker + form id.
 * @param {Element} modalContent
 * @param {string | null} selectedVariantId
 */
function syncVariantSelection(modalContent, selectedVariantId) {
  if (!selectedVariantId) return;

  const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
  for (const input of modalInputs) {
    if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      break;
    }
  }

  const idInput = modalContent.querySelector('product-form-component input[name="id"]');
  if (idInput instanceof HTMLInputElement && idInput.value !== selectedVariantId) {
    idInput.value = selectedVariantId;
  }
}

/**
 * Visible picker selections (Size, Slats, …). Color is sibling-driven / hidden.
 * Prefer drawer labels — they always reflect the UI even if radio state is briefly stale.
 * @param {ParentNode} root
 * @returns {string[]}
 */
function getSelectedOptionValues(root) {
  /** @type {string[]} */
  const values = [];
  const seen = new Set();

  for (const el of root.querySelectorAll('variant-picker .variant-option__drawer-value')) {
    const text = el.textContent?.trim();
    if (text && !seen.has(text)) {
      seen.add(text);
      values.push(text);
    }
  }

  for (const input of root.querySelectorAll('variant-picker fieldset input[type="radio"]:checked')) {
    if (!(input instanceof HTMLInputElement) || !input.value) continue;
    if (seen.has(input.value)) continue;
    seen.add(input.value);
    values.push(input.value);
  }

  return values;
}

/**
 * Find an available variant on a product grid whose options include every preserved label.
 * @param {ParentNode} root
 * @param {string[]} optionValues
 * @returns {string | null}
 */
function findMatchingVariantId(root, optionValues) {
  if (!optionValues.length) return null;

  const script = root.querySelector('script[data-all-variants]');
  if (!script?.textContent) return null;

  try {
    const variants = JSON.parse(script.textContent);
    if (!Array.isArray(variants)) return null;

    const match = variants.find((variant) => {
      if (!variant?.available) return false;
      const opts = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return optionValues.every((val) => opts.includes(val));
    });

    return match?.id != null ? String(match.id) : null;
  } catch {
    return null;
  }
}

/**
 * Client-side fallback: check matching option radios + form id, then fire change after CE upgrade.
 * @param {Element} modalContent
 * @param {string[] | null | undefined} optionValues
 */
function syncPreservedOptionValues(modalContent, optionValues) {
  if (!optionValues?.length) return;

  const matchedVariantId = findMatchingVariantId(modalContent, optionValues);

  /** @type {HTMLInputElement | null} */
  let lastChecked = null;

  for (const value of optionValues) {
    const match = Array.from(
      modalContent.querySelectorAll('variant-picker fieldset input[type="radio"]')
    ).find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;
      if (input.value !== value) return false;
      if (input.disabled || input.dataset.optionAvailable === 'false') return false;
      return true;
    });

    if (!(match instanceof HTMLInputElement)) continue;
    match.checked = true;
    lastChecked = match;

    const triggerValue = match.closest('fieldset')?.querySelector('.variant-option__drawer-value');
    if (triggerValue) {
      triggerValue.textContent = match.value;
    }
  }

  const variantId = matchedVariantId || lastChecked?.dataset.variantId;
  if (variantId) {
    const idInput = modalContent.querySelector('product-form-component input[name="id"]');
    if (idInput instanceof HTMLInputElement) {
      idInput.value = variantId;
    }
  }

  if (!lastChecked) return;

  // Morph just created the picker — wait for the custom element + change listener.
  const radio = lastChecked;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!radio.isConnected) return;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

export class QuickAddComponent extends Component {
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /**
   * Prefetch Quick Add section HTML into the shared cache (desktop product-card hover).
   * Public API for `product-card.js` — do not remove or privatize.
   * @param {string} productPageUrl
   * @returns {Promise<void>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    const cacheKey = productCacheKey(productPageUrl);
    if (productGridCache.has(cacheKey)) return;

    try {
      const response = await fetch(buildQuickAddSectionUrl(cacheKey));
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const gridElement = extractQuickAddGrid(html);
      if (!gridElement) return;

      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      stripDeliveryDrawerFromModal(productGrid);
      productGridCache.set(cacheKey, productGrid);
    } catch {
      // Prefetch is best-effort; open path fetches again on miss.
    }
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();
    const baseHref = productLink?.href || this.dataset.productUrl || '';

    if (!baseHref) return '';

    const url = new URL(baseHref, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const fromCard = productCard?.getSelectedVariantId();
    if (fromCard) return fromCard;

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      return variantInput.value;
    }

    return this.dataset.variantId || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#cartUpdateAbortController.abort();
  }

  #handleCartUpdate = () => {
    productGridCache.clear();
  };

  /**
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const trigger = event.currentTarget;
    if (trigger instanceof HTMLButtonElement && trigger.disabled) return;

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const loaded = await dialogComponent.loadProduct(currentUrl, {
      productTitle: this.dataset.productTitle || '',
      syncVariantId: this.#getSelectedVariantId(),
      contextTitle: this.dataset.quickAddContextTitle?.trim() || '',
      dlFlag: this.dataset.dlModalFlag || this.dataset.dlFlag || '',
    });

    if (!loaded) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
    dialogComponent.resetScroll();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute('stay-visible', false);
      },
      { once: true }
    );
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {AbortController | null} */
  #fetchAbortController = null;
  /** @type {string} */
  #contextTitle = '';
  /** dataLayer ATC flag stamped onto the modal product form after morph (e.g. Pairs well with). */
  #pendingDlFlag = '';
  /** Monotonic token so a slower/aborted loadProduct cannot morph or clear loading. */
  #loadGeneration = 0;
  /** @type {(() => Promise<void>) | null} */
  #baseCloseDialog = null;

  connectedCallback() {
    // Wrap DialogComponent.closeDialog (class field) — `super.closeDialog` is unreliable on field arrows.
    if (!this.#baseCloseDialog) {
      this.#baseCloseDialog = this.closeDialog.bind(this);
      this.closeDialog = async () => {
        const modal = this.refs.dialog;
        if (modal instanceof HTMLDialogElement) {
          forceClosePortedDrawers(modal);
        } else {
          forceClosePortedDrawers(this);
        }
        await this.#baseCloseDialog?.();
      };
    }

    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#staticifyDeliveryAfterVariantUpdate, {
      signal: this.#abortController.signal,
    });
    // media-gallery replaceWith drops chrome — re-pin packshot overlays + mobile strip.
    this.addEventListener(ThemeEvents.variantUpdate, this.#refreshGalleryAfterVariantUpdate, {
      signal: this.#abortController.signal,
    });
    // After picker morph, server HTML restores colliding vo-drawer-* ids — re-scope.
    this.addEventListener(ThemeEvents.variantUpdate, this.#rescopeVariantDrawersAfterUpdate, {
      signal: this.#abortController.signal,
    });
    // Capture on the real `<dialog>` — `#quick-add-dialog { display: contents }` can drop
    // bubbled clicks, and AS rows live in a portaled nested panel.
    const modal = this.refs.dialog;
    if (modal instanceof HTMLDialogElement) {
      modal.addEventListener('click', this.#handleCrossProductClick, {
        signal: this.#abortController.signal,
        capture: true,
      });
    } else {
      this.addEventListener('click', this.#handleCrossProductClick, { signal: this.#abortController.signal });
    }
    this.addEventListener(DialogOpenEvent.eventName, this.#onQuickAddOpened, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, {
      signal: this.#abortController.signal,
    });

    // Klarna OSM learn-more interstitial mounts on body under our top-layer <dialog>.
    this.#bindKlarnaInformationalModalClose();
  }

  #onQuickAddOpened = () => {
    const modalContent =
      this.querySelector('[data-quick-add-modal-content]') ||
      this.querySelector('#quick-add-modal-content');
    if (modalContent) scopeVariantDrawersForQuickAdd(modalContent);
  };

  /**
   * Klarna learn-more mounts under our top-layer dialog — close quick-add when it opens.
   * @see https://docs.klarna.com/acquirer/klarna/on-site-messaging/additional-resources/osm-exposed-events/
   */
  #bindKlarnaInformationalModalClose() {
    const { signal } = this.#abortController;

    const bind = () => {
      const on = window.Klarna?.OnsiteMessaging?.on;
      if (typeof on !== 'function') return false;

      on.call(window.Klarna.OnsiteMessaging, 'informationalModalOpened', () => {
        const modal = this.refs.dialog;
        if (modal instanceof HTMLDialogElement && modal.open) void this.closeDialog();
      });
      return true;
    };

    if (bind()) return;

    // Klarna lib.js loads async — poll briefly until OnsiteMessaging is ready.
    const id = window.setInterval(() => {
      if (signal.aborted || bind()) window.clearInterval(id);
    }, 500);
    window.setTimeout(() => window.clearInterval(id), 15000);
    signal.addEventListener('abort', () => window.clearInterval(id), { once: true });
  }

  /**
   * Open size/option drawer as a non-modal panel inside the quick-add dialog.
   * showModal() puts the panel in the top layer (escapes overflow); we portal the host
   * into `.quick-add-modal` and use dialog.show() so the slide is clipped to the modal edge.
   * @param {Event} event
   */
  openVariantDrawer = (event) => {
    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) return;

    const trigger = eventTarget.closest('[data-variant-drawer-trigger]');
    if (!(trigger instanceof HTMLElement)) return;
    if (!this.contains(trigger)) return;

    const controlsId = trigger.getAttribute('aria-controls');
    let host = controlsId ? document.getElementById(controlsId) : null;
    if (!(host instanceof HTMLElement)) {
      host =
        trigger.closest('fieldset')?.querySelector('[data-quick-add-variant-drawer]') ??
        trigger.closest('fieldset')?.querySelector('dialog-component.dev-drawer') ??
        trigger.closest('[data-dev-as]')?.querySelector('[data-quick-add-variant-drawer]') ??
        trigger.closest('[data-dev-as]')?.querySelector('dialog-component.dev-drawer') ??
        null;
    }
    if (!(host instanceof HTMLElement) || !this.contains(host)) return;

    this.openVariantDrawerFromHost(host);
  };

  /**
   * @param {HTMLElement} host
   */
  openVariantDrawerFromHost = (host) => {
    const modal = this.refs.dialog;
    const drawerDialog = getDrawerDialog(host);
    if (!(modal instanceof HTMLDialogElement) || !(drawerDialog instanceof HTMLDialogElement)) return;
    if (drawerDialog.open) return;

    portalDrawerIntoModal(host, modal);
    host.setAttribute('data-qa-drawer-open', '');

    // Nested panel must never participate in body scroll-lock (quick-add already owns it).
    drawerDialog.removeAttribute('scroll-lock');
    // Marks non-modal panel for scroll-lock handoff fallback when `:modal` is unsupported.
    drawerDialog.setAttribute('data-qa-non-modal', '');

    // Geometry lives in `quick-add-modal.liquid` under `[data-qa-drawer-ported]`; motion is the
    // shared `.dialog-drawer` keyframes. Never write `transform` inline — an inline `!important`
    // declaration outranks the animation origin and the panel would never travel.
    drawerDialog.classList.remove('dialog-closing', 'dialog-visible');

    // Park before the panel becomes visible, exactly as `DialogComponent.showDialog()` does.
    drawerDialog.classList.add('dialog-enter-hold');
    drawerDialog.show();
    host.dispatchEvent(new DialogOpenEvent());
    releaseQaDrawerEntry(drawerDialog);

    /** @returns {Promise<void>} */
    const closePanel = () => closeQaDrawer(drawerDialog);

    /** @param {MouseEvent} event */
    const onHostClick = (event) => {
      if (!event.composedPath().includes(drawerDialog)) closePanel();
    };
    /** @param {KeyboardEvent} event */
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      closePanel();
    };

    host.addEventListener('click', onHostClick);
    host.addEventListener('keydown', onKeyDown);

    drawerDialog.addEventListener(
      'close',
      () => {
        cancelQaDrawerAnimation(drawerDialog);
        host.removeEventListener('click', onHostClick);
        host.removeEventListener('keydown', onKeyDown);
        drawerDialog.removeAttribute('data-qa-non-modal');
        restoreDrawerFromModal(host);
      },
      { once: true }
    );
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#fetchAbortController?.abort();
    headingScrollControllers.get(this)?.abort();
    headingScrollControllers.delete(this);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  resetScroll() {
    const productDetails =
      this.querySelector('[data-quick-add-details]') || this.querySelector('.quick-add-grid__details');
    const productMediaScroll =
      this.querySelector('[data-quick-add-media-scroll]') ||
      qaFind(this, '[data-product-media-column]', '.quick-add-grid__media');
    const modalContent =
      this.querySelector('[data-quick-add-modal-content]') ||
      this.querySelector('#quick-add-modal-content');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMediaScroll?.scrollTo({ top: 0, behavior: 'instant' });
    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
    this.#rewindGallery();
  }

  /**
   * Send the mobile media strip back to the first image.
   *
   * `DialogComponent` leaves horizontal carousels alone, but morph reuses this scroller node and the
   * browser preserves its `scrollLeft` across `display: none`, so reopening resumed on the last
   * swiped image. Must be deferred a frame: `showDialog()` also defers `showModal()`, so a write
   * from here is dropped and then overwritten by the browser's own restore.
   */
  #rewindGallery() {
    requestAnimationFrame(() => {
      const slides = this.querySelector('slideshow-slides');
      if (slides instanceof HTMLElement) {
        // Reading first forces the restore to land, so the write below outlives it.
        void slides.scrollLeft;
        slides.scrollLeft = 0;
      }

      // Also rewinds the page counter, dots and `aria-hidden`, which the raw offset leaves stale.
      const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (
        this.querySelector('slideshow-component')
      );
      slideshow?.select(0, undefined, { animate: false });
    });
  }

  /**
   * Fetch `dev-quick-add` section HTML and morph into the modal.
   * @param {string} productPageUrl
   * @param {{
   *   productTitle?: string,
   *   syncVariantId?: string | null,
   *   preserveOptionValues?: string[] | null,
   *   contextTitle?: string,
   *   dlFlag?: string,
   * }} [options]
   * @returns {Promise<boolean>}
   */
  async loadProduct(productPageUrl, options = {}) {
    if (!productPageUrl) return false;

    if (typeof options.contextTitle === 'string') {
      this.#contextTitle = options.contextTitle;
    }

    // Preserve prior flag across color-sibling remorphs when caller omits dlFlag.
    if (typeof options.dlFlag === 'string') {
      this.#pendingDlFlag = options.dlFlag;
    }

    const generation = ++this.#loadGeneration;
    this.toggleAttribute('data-loading', true);

    try {
      const cacheKey = productCacheKey(productPageUrl);
      let productGrid = productGridCache.get(cacheKey);

      if (!productGrid) {
        const html = await this.#fetchProductPage(cacheKey);
        if (generation !== this.#loadGeneration) return false;
        if (!html) return false;

        const gridElement = extractQuickAddGrid(html);
        if (!gridElement) return false;

        productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        stripDeliveryDrawerFromModal(productGrid);
        productGridCache.set(cacheKey, productGrid);
      }

      /** @type {Element} */
      let freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      stripDeliveryDrawerFromModal(freshContent);

      // Color sibling: resolve the same option labels to a variant id, then re-fetch
      // that variant so Liquid already marks the correct size (avoids change-race after morph).
      const preserveOptionValues = options.preserveOptionValues ?? null;
      const syncVariantId = options.syncVariantId ?? null;

      /** @type {string | null} */
      let variantFetchId = null;
      if (preserveOptionValues?.length) {
        variantFetchId = findMatchingVariantId(freshContent, preserveOptionValues);
      } else if (syncVariantId) {
        // PWW / card pin: fetch that variant so Liquid + picker JSON match ATC.
        variantFetchId = syncVariantId;
      }

      if (variantFetchId) {
        const variantUrl = new URL(cacheKey, window.location.origin);
        variantUrl.searchParams.set('variant', variantFetchId);
        const variantHtml = await this.#fetchProductPage(variantUrl.toString());
        if (generation !== this.#loadGeneration) return false;

        const variantGrid = variantHtml ? extractQuickAddGrid(variantHtml) : null;
        if (variantGrid) {
          freshContent = /** @type {Element} */ (variantGrid.cloneNode(true));
          stripDeliveryDrawerFromModal(freshContent);
        }
        // Non-abort miss/error: keep base HTML and pin via syncVariantSelection / form id.
      }

      if (generation !== this.#loadGeneration) return false;

      await this.#applyProductGrid(freshContent, {
        syncVariantId,
        preserveOptionValues,
        dlFlag: this.#pendingDlFlag,
      });

      if (generation !== this.#loadGeneration) return false;

      const titleFromContent =
        this.querySelector('[data-quick-add-header] a')?.textContent?.trim() ||
        this.querySelector('a[data-quick-add-heading]')?.textContent?.trim() ||
        options.productTitle ||
        '';
      // Product name wins over the opener's context title (e.g. Pairs well with) so the
      // sheet reads the same from a product card and from a recommendation row.
      const titlebarText = titleFromContent || this.#contextTitle;
      this.#setTitlebarLabel(titlebarText);

      return true;
    } catch (error) {
      if (generation !== this.#loadGeneration) return false;
      console.error('[quick-add] loadProduct failed', error);
      return false;
    } finally {
      if (generation === this.#loadGeneration) {
        this.removeAttribute('data-loading');
      }
    }
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #fetchProductPage(productPageUrl) {
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    try {
      const response = await fetch(buildQuickAddSectionUrl(productPageUrl), {
        signal: this.#fetchAbortController.signal,
      });

      if (!response.ok) {
        console.error(`[quick-add] Failed to fetch quick-add section: HTTP ${response.status}`);
        return null;
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      console.error('[quick-add] Failed to fetch quick-add section', error);
      return null;
    } finally {
      this.#fetchAbortController = null;
    }
  }

  /**
   * @param {Element} productGrid
   * @param {{
   *   syncVariantId?: string | null,
   *   preserveOptionValues?: string[] | null,
   *   dlFlag?: string,
   * }} options
   */
  async #applyProductGrid(productGrid, options) {
    const modalContent =
      document.querySelector('[data-quick-add-modal-content]') ||
      document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    // Ported drawers live outside the morph root — close/restore before replacing HTML.
    const modal = this.refs.dialog;
    if (modal instanceof HTMLDialogElement) {
      forceClosePortedDrawers(modal);
    }

    morph(modalContent, productGrid);

    // Morph keeps the live host node — re-apply section root hooks lost on attribute merge.
    modalContent.classList.add('quick-add-grid', 'wg-translate');
    modalContent.setAttribute('data-quick-add-grid', '');
    modalContent.setAttribute('data-quick-add-modal-content', '');

    // Sibling color swap: prefer matching size labels over a pinned variant id.
    if (options.preserveOptionValues?.length) {
      syncPreservedOptionValues(modalContent, options.preserveOptionValues);
    } else {
      syncVariantSelection(modalContent, options.syncVariantId ?? null);
    }
    enhanceQuickAddModal(modalContent);

    // Product-card opens often sync a variant → morph replaces drawers; re-wire triggers.
    scopeVariantDrawersForQuickAdd(modalContent);
    bindQuickAddHeadingScroll(this);

    // Stamp ATC analytics flag on modal form only (not on choose-button open).
    const modalForm = modalContent.querySelector('product-form-component');
    if (modalForm instanceof HTMLElement) {
      const flag = typeof options.dlFlag === 'string' ? options.dlFlag.trim() : '';
      if (flag) {
        modalForm.setAttribute('data-dl-flag', flag);
      } else {
        modalForm.removeAttribute('data-dl-flag');
      }
    }
  }

  /** @param {string} title */
  #setTitlebarLabel(title) {
    const label = this.querySelector('[data-quick-add-titlebar-label]');
    if (!(label instanceof HTMLElement)) return;
    label.textContent = title;
  }

  /** Re-unique drawer ids after variant-picker morph replaces markup from the server. */
  #rescopeVariantDrawersAfterUpdate = () => {
    const modalContent =
      this.querySelector('[data-quick-add-modal-content]') ||
      this.querySelector('#quick-add-modal-content');
    if (modalContent) {
      scopeVariantDrawersForQuickAdd(modalContent);
    }
    // Morph can leave the picker stuck inert from a prior variant:selected.
    for (const picker of this.querySelectorAll('variant-picker[inert]')) {
      picker.inert = false;
    }
    // Refresh size/drawer availability against the new current variant (hidden Color, etc.).
    for (const fieldset of this.querySelectorAll('.variant-option--drawer-capable')) {
      const drawer = /** @type {HTMLElement & { __devVoDrawer?: { _updateDrawerItemAvailability?: () => void } }} */ (
        fieldset
      ).__devVoDrawer;
      drawer?._updateDrawerItemAvailability?.();
    }
  };

  /**
   * Cross-product links (color siblings + additional swatches) — remorph modal instead of navigating away.
   * @param {MouseEvent} event
   */
  #handleCrossProductClick = async (event) => {
    if (event.button != null && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    /** @param {string} selector */
    const fromPath = (selector) => {
      for (const node of path) {
        if (node instanceof Element && node.matches(selector)) return node;
      }
      return event.target instanceof Element ? event.target.closest(selector) : null;
    };

    const colorLink = /** @type {HTMLAnchorElement | null} */ (fromPath('[data-quick-add-color-sibling]'));
    const asLink = /** @type {HTMLAnchorElement | null} */ (fromPath('[data-quick-add-additional-swatch]'));
    const link = colorLink || asLink;
    if (!(link instanceof HTMLAnchorElement)) return;

    // Ignore active / unavailable targets (same product or disabled row)
    if (colorLink?.closest('.cs-item.active')) return;
    if (asLink?.getAttribute('aria-current') === 'page') return;
    if (
      asLink?.hasAttribute('disabled') ||
      asLink?.getAttribute('aria-disabled') === 'true' ||
      asLink?.classList.contains('dev-as-drawer__link--unavailable')
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const handle = link.getAttribute('data-product-handle');
    const href = handle
      ? new URL(
          `${window.Shopify?.routes?.root || '/'}products/${handle}`.replace(/\/{2,}/g, '/'),
          window.location.origin
        ).toString()
      : link.href;
    if (!href) return;

    // Color sibling: carry Size (etc.) when the same label exists & is in stock.
    // AS size switch: preserve other native options when present on the destination product.
    const preserveOptionValues = getSelectedOptionValues(this);
    const url = new URL(canonicalProductPageUrl(href), window.location.origin);
    url.searchParams.delete('variant');
    const loaded = await this.loadProduct(url.toString(), { preserveOptionValues });

    if (loaded) {
      this.resetScroll();
    }
  };

  /**
   * @param {CartUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const html = event.detail.data.html;
    const anchorElement = /** @type {HTMLAnchorElement | null} */ (
      html?.querySelector('a[data-quick-add-heading]') || html?.querySelector('.view-product-title a')
    );
    const productTitleLink = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('[data-quick-add-header] a[data-quick-add-heading]') ||
        this.querySelector('a[data-quick-add-heading]')
    );

    if (!anchorElement || !productTitleLink) return;

    productTitleLink.href = anchorElement.href;
  };

  #staticifyDeliveryAfterVariantUpdate = () => {
    const modalContent =
      document.querySelector('[data-quick-add-modal-content]') ||
      document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    requestAnimationFrame(() => {
      stripDeliveryDrawerFromModal(modalContent);
      staticifyDeliveryRows(modalContent);
    });
  };

  #refreshGalleryAfterVariantUpdate = (/** @type {CustomEvent} */ event) => {
    const modalContent =
      this.querySelector('[data-quick-add-modal-content]') ||
      this.querySelector('#quick-add-modal-content');
    if (!modalContent) return;

    const scope = quickAddDialogScope(modalContent);

    // Belt-and-suspenders: ensure packshot gallery matches variant HTML.
    // On mobile the gallery lives in the shelled column outside modal content.
    const source = event.detail?.data?.html;
    const freshGallery = source instanceof Document ? source.querySelector('media-gallery') : null;
    const currentGallery = scope.querySelector('media-gallery');
    if (freshGallery && currentGallery && freshGallery !== currentGallery) {
      currentGallery.replaceWith(freshGallery);
    }

    requestAnimationFrame(() => {
      refreshQuickAddGalleryAfterVariant(modalContent);
    });
  };

  #handleDialogClose = () => {
    this.#teardownModalChrome();

    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  /** Drawers + titlebar state — safe to call after close or before remorph. */
  #teardownModalChrome() {
    const modal = this.refs.dialog;
    if (modal instanceof HTMLDialogElement) {
      forceClosePortedDrawers(modal);
    }

    modal?.classList.remove('quick-add-modal--heading-scrolled');
    this.#contextTitle = '';
    headingScrollControllers.get(this)?.abort();
    headingScrollControllers.delete(this);
  }
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
